import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CeldaProducto } from '@/components/ui/celda-producto';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Etiqueta } from '@/components/ui/etiqueta';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { Input } from '@/components/ui/input';
import { Precio } from '@/components/ui/precio';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { cantidadDe, formatoCantidad, formatoFecha } from '@/lib/utils/formato-precio';
import { strategyApplicationsApi } from '../../services/strategy-applications-api';
import type {
  EstrategiaEnBorradorBody,
  FaltaParaCalcular,
  GroupDraft,
  ProductoDelAlcance,
  ProductoSimulado,
  StrategyKind,
} from '../../types/strategy-applications.types';

/**
 * Los «Ver cuáles (N)» de «Crear una estrategia» (agregados del 29-sep, spec
 * 232, A2 y A3). Sólo informan: qué hacer con los que ya tienen otra estrategia
 * se sigue decidiendo para todos juntos en el aviso del paso 3.
 */

/** Cuántos se piden de una vez: la ventana es para mirar, no para exportar. */
const TOPE = 200;

const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Qué le falta, y cómo se arregla (A3). La causa sale del backend, no de adivinar. */
export const QUE_FALTA: Record<FaltaParaCalcular, { texto: string; arreglo: string }> = {
  COMPETENCIA: {
    texto: 'Sin competencia leída',
    arreglo:
      'Ninguna tienda seguida lo vende, o no se encontró. Vinculalo en Inferencia o sumá una tienda.',
  },
  COSTO: { texto: 'Sin costo cargado', arreglo: 'Cargale el costo en Precios Dinámicos.' },
  PRECIO_DE_LISTA: {
    texto: 'Sin precio de lista',
    arreglo: 'Cargale el precio de lista en Precios Dinámicos.',
  },
  PRECIO_IMPOSIBLE: {
    texto: 'Conflicto entre reglas',
    arreglo:
      'El precio que pide es de cero o menos: revisá su margen o su fórmula en Precios Dinámicos.',
  },
  OTRO: {
    texto: 'La estrategia no pudo decidir un precio',
    arreglo: 'Revisá sus datos en Precios Dinámicos.',
  },
};

/** La frase de «A N no se les puede calcular: …» cuando falta una sola cosa. */
const UNA_CAUSA: Record<FaltaParaCalcular, [string, string]> = {
  COMPETENCIA: ['no hay competencia leída', 'no hay competencia leída'],
  COSTO: ['no tiene costo cargado', 'no tienen costo cargado'],
  PRECIO_DE_LISTA: ['no tiene precio de lista', 'no tienen precio de lista'],
  PRECIO_IMPOSIBLE: ['tiene un conflicto entre reglas', 'tienen un conflicto entre reglas'],
  OTRO: ['la estrategia no pudo decidir un precio', 'la estrategia no pudo decidir un precio'],
};
/** Y cuando son varias: «2 sin competencia leída · 1 sin costo cargado». */
const VARIAS: Record<FaltaParaCalcular, string> = {
  COMPETENCIA: 'sin competencia leída',
  COSTO: 'sin costo cargado',
  PRECIO_DE_LISTA: 'sin precio de lista',
  PRECIO_IMPOSIBLE: 'con un conflicto entre reglas',
  OTRO: 'por otra causa',
};
const ORDEN: FaltaParaCalcular[] = [
  'COMPETENCIA',
  'COSTO',
  'PRECIO_DE_LISTA',
  'PRECIO_IMPOSIBLE',
  'OTRO',
];

/**
 * «A 2 no se les puede calcular: no hay competencia leída.» (A3): lo que falta
 * de verdad. Con un servidor viejo (sin el porqué), la frase de antes.
 */
export function fraseDeLoQueFalta(
  sinDatos: number,
  porMotivo: Partial<Record<FaltaParaCalcular, number>> | undefined
): string {
  const cabeza =
    sinDatos === 1
      ? 'A 1 no se le puede calcular'
      : `A ${formatoCantidad(sinDatos)} no se les puede calcular`;
  if (!porMotivo) {
    return `${cabeza}: ${sinDatos === 1 ? 'le' : 'les'} falta competencia, costo o precio de lista.`;
  }
  const hay = ORDEN.filter((k) => (porMotivo[k] ?? 0) > 0);
  if (hay.length === 1) return `${cabeza}: ${UNA_CAUSA[hay[0]][sinDatos === 1 ? 0 : 1]}.`;
  return `${cabeza}: ${hay.map((k) => `${formatoCantidad(porMotivo[k]!)} ${VARIAS[k]}`).join(' · ')}.`;
}

/** Qué necesita el método, para el subtítulo de la ventana. */
function loQueNecesita(metodo: StrategyKind, titulo: string): string {
  if (metodo === 'MARGIN_TARGET') return `«${titulo}» necesita el costo de cada producto.`;
  if (metodo === 'DISCOUNT_PERCENT')
    return `«${titulo}» necesita el precio de lista de cada producto.`;
  return `«${titulo}» necesita al menos un precio de la competencia leído.`;
}

/** El buscador de las listas de productos del asistente (también «Resultados esperados»). */
export function BuscadorDeProductos({
  valor,
  onCambiar,
}: {
  valor: string;
  onCambiar: (v: string) => void;
}) {
  return (
    <div className="relative w-64">
      <Search
        aria-hidden="true"
        className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
      />
      <Input
        type="search"
        className="pl-8"
        placeholder="Buscar por nombre o código…"
        aria-label="Buscar un producto"
        value={valor}
        onChange={(e) => onCambiar(e.target.value)}
      />
    </div>
  );
}

function Cargando() {
  return (
    <div className="space-y-2" aria-label="Cargando">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/** Pide algo al abrir la ventana, con «Reintentar» si falla. */
function useAlAbrir<T>(abierto: boolean, pedir: () => Promise<T>) {
  const [estado, setEstado] = useState<
    { tipo: 'CARGANDO' } | { tipo: 'LISTO'; data: T } | { tipo: 'ERROR'; error: unknown }
  >({ tipo: 'CARGANDO' });
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    if (!abierto) return;
    let vivo = true;
    setEstado({ tipo: 'CARGANDO' });
    pedir()
      .then((data) => vivo && setEstado({ tipo: 'LISTO', data }))
      .catch((error: unknown) => vivo && setEstado({ tipo: 'ERROR', error }));
    return () => {
      vivo = false;
    };
    // `pedir` cambia en cada render: lo que manda es abrir o reintentar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, vuelta]);
  const reintentar = useCallback(() => setVuelta((v) => v + 1), []);
  return { estado, reintentar };
}

/**
 * A2 · «N ya tienen otra estrategia» → «Ver cuáles (N)»: cada producto, su
 * categoría, la estrategia que lo tiene hoy, desde cuándo y su precio de hoy,
 * con buscador y filtro por estrategia. `desde` es el día en que se creó esa
 * estrategia: cuándo entró cada producto no se guarda (ruling R19).
 */
export function VerCualesYaTienenOtra({
  grupo,
  cuantos,
  enlace = false,
}: {
  grupo: GroupDraft;
  cuantos: number;
  /** Maqueta agregados.html#paso4: en el aviso del paso 4 va como enlace, no como botón. */
  enlace?: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [estrategia, setEstrategia] = useState('TODAS');
  const { estado, reintentar } = useAlAbrir(abierto, () =>
    strategyApplicationsApi.productosDelAlcance(grupo, 1, TOPE, undefined, { soloConOtra: true })
  );
  const productos: ProductoDelAlcance[] = useMemo(
    () => (estado.tipo === 'LISTO' ? estado.data.productos : []),
    [estado]
  );
  const total = estado.tipo === 'LISTO' ? estado.data.total : cuantos;
  const porEstrategia = useMemo(() => {
    const m = new Map<string, { nombre: string; cuantos: number }>();
    for (const p of productos) {
      if (!p.yaLaTiene) continue;
      const e = m.get(p.yaLaTiene.applicationId);
      if (e) e.cuantos++;
      else m.set(p.yaLaTiene.applicationId, { nombre: p.yaLaTiene.applicationName, cuantos: 1 });
    }
    return [...m].sort((a, b) => b[1].cuantos - a[1].cuantos);
  }, [productos]);
  const texto = sinTildes(q.trim());
  const visibles = productos.filter(
    (p) =>
      (estrategia === 'TODAS' || p.yaLaTiene?.applicationId === estrategia) &&
      (!texto || sinTildes(`${p.name} ${p.sku}`).includes(texto))
  );

  return (
    <>
      {enlace ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto px-0"
          onClick={() => setAbierto(true)}
        >
          Ver cuáles
        </Button>
      ) : (
        <Button type="button" size="sm" variant="outline" onClick={() => setAbierto(true)}>
          Ver cuáles ({formatoCantidad(cuantos)})
        </Button>
      )}
      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {cantidadDe(total, 'producto ya tiene', 'productos ya tienen')} otra estrategia
            </DialogTitle>
            <DialogDescription>
              Un producto tiene una sola estrategia. Qué hacer con ellos se decide para todos
              juntos, en el aviso.
            </DialogDescription>
          </DialogHeader>
          {estado.tipo === 'ERROR' ? (
            <EstadoError onReintentar={reintentar}>
              {mensajeDeError(estado.error, 'No se pudo traer cuáles son.')}
            </EstadoError>
          ) : estado.tipo === 'CARGANDO' ? (
            <Cargando />
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <BuscadorDeProductos valor={q} onCambiar={setQ} />
                {porEstrategia.length > 1 && (
                  <GrupoDeOpciones
                    aria-label="Estrategia"
                    valor={estrategia}
                    onCambiar={setEstrategia}
                    opciones={[
                      { valor: 'TODAS', texto: 'Todas', cuenta: total },
                      ...porEstrategia.map(([id, e]) => ({
                        valor: id,
                        texto: `«${e.nombre}»`,
                        cuenta: e.cuantos,
                      })),
                    ]}
                  />
                )}
              </div>
              <div className="max-h-[55vh] overflow-y-auto rounded-lg border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>La tiene hoy</TableHead>
                      <TableHead>Desde</TableHead>
                      <TableHead className="text-right">Hoy (Bs)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                          Ningún producto con esa búsqueda.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibles.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>
                            <CeldaProducto nombre={p.name} detalle={p.sku} />
                          </TableCell>
                          <TableCell>{p.category ?? '—'}</TableCell>
                          <TableCell>«{p.yaLaTiene?.applicationName}»</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {p.yaLaTiene?.desde ? formatoFecha(p.yaLaTiene.desde) : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Precio valor={p.currentPrice} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">
                «Desde» es el día en que se creó esa estrategia, o en que empieza si todavía no
                empezó.
                {total > productos.length &&
                  ` Se ven los primeros ${formatoCantidad(productos.length)} de ${formatoCantidad(total)}: buscá por nombre o SKU para encontrar otro.`}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A3 · «A N no se les puede calcular» → «Ver cuáles (N)»: cada producto con lo
 * que le falta de verdad según el método, y cómo se arregla. Es la misma
 * simulación, con el detalle (la cuenta no cambia).
 */
export function VerCualesNoSeCalculan({
  body,
  cuantos,
  tituloDelMetodo,
}: {
  body: EstrategiaEnBorradorBody;
  cuantos: number;
  /** «Igualar al más barato»: lo pasa el paso 2, que tiene los métodos. */
  tituloDelMetodo: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const { estado, reintentar } = useAlAbrir(abierto, () =>
    strategyApplicationsApi.simular({ ...body, conDetalle: true })
  );
  const productos: ProductoSimulado[] =
    estado.tipo === 'LISTO'
      ? (estado.data.detalle ?? []).filter((p) => p.resultado === 'NO_SE_PUEDE_CALCULAR')
      : [];

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setAbierto(true)}>
        Ver cuáles ({formatoCantidad(cuantos)})
      </Button>
      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {cantidadDe(
                cuantos,
                'producto no se puede calcular',
                'productos no se pueden calcular'
              )}
            </DialogTitle>
            <DialogDescription>{loQueNecesita(body.strategy, tituloDelMetodo)}</DialogDescription>
          </DialogHeader>
          {estado.tipo === 'ERROR' ? (
            <EstadoError onReintentar={reintentar}>
              {mensajeDeError(estado.error, 'No se pudo traer cuáles son.')}
            </EstadoError>
          ) : estado.tipo === 'CARGANDO' ? (
            <Cargando />
          ) : productos.length === 0 ? (
            <EstadoVacio titulo="Ahora todos se pueden calcular">
              Algo cambió desde que se contaron: la cuenta de arriba se pone al día sola.
            </EstadoVacio>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead>Qué le falta</TableHead>
                    <TableHead>Cómo se arregla</TableHead>
                    <TableHead className="text-right">Hoy (Bs)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productos.map((p) => {
                    const falta = QUE_FALTA[p.falta ?? 'OTRO'];
                    return (
                      <TableRow key={p.productId}>
                        <TableCell>
                          <CeldaProducto nombre={p.name} detalle={p.sku} />
                        </TableCell>
                        <TableCell>
                          <Etiqueta tono="alerta">{falta.texto}</Etiqueta>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{falta.arreglo}</TableCell>
                        <TableCell className="text-right">
                          <Precio valor={p.precioHoy} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
